import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'antd';
import { BarChart3, Pencil, Plus, Tags, Trash2 } from 'lucide-react';

import { ROUTE_URLS } from '../../routes';
import { createLink, searchFn } from '../../utils';
import { useBranches, useDeleteBranch } from '../../hooks';
import { DataTable, DeleteConfirmModal, PageHeader, RowActions, SearchInput } from '../../components';
import { useI18n } from '../../i18n';

export const Branches = () => {
  const { isAdmin } = useSelector(state => state.authReducer);
  const { t } = useI18n();
  const { data: branches, isLoading, isError, refetch } = useBranches();
  const { mutateAsync: deleteBranch } = useDeleteBranch();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () => (Array.isArray(branches) ? searchFn(branches, search) : []),
    [branches, search],
  );

  const getComponent = useCallback(
    (route, data, className) => {
      const text = data.login || data.name;
      if (isAdmin) {
        return (
          <Link to={createLink(route, data.id)} className={className}>
            {text}
          </Link>
        );
      }
      return <span className={className}>{text}</span>;
    },
    [isAdmin],
  );

  const columns = useMemo(
    () => [
      {
        title: t('branches.name'),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (text, branch) => (
          <div className='wm-cell-main'>
            <span className='wm-cell-icon'>{branch.name.slice(0, 2)}</span>
            {getComponent(ROUTE_URLS.EDIT_BRANCH, branch, 'wm-link-strong')}
          </div>
        ),
      },
      {
        title: t('branches.staff'),
        dataIndex: 'users',
        render: users =>
          users?.length ? (
            <div className='wm-chips'>
              {users.map(user => (
                <span key={user.id}>
                  {getComponent(ROUTE_URLS.EDIT_USER, user, `wm-chip${isAdmin ? ' wm-chip--link' : ''}`)}
                </span>
              ))}
            </div>
          ) : (
            <span className='wm-muted'>{t('branches.noStaff')}</span>
          ),
      },
      {
        title: t('common.actions'),
        dataIndex: 'id',
        align: 'right',
        width: 280,
        render: id => (
          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <Link to={createLink(ROUTE_URLS.BRANCH_PRICES, id)}>
              <Button size='small' icon={<Tags size={14} />}>
                {t('branches.prices')}
              </Button>
            </Link>
            <Link to={createLink(ROUTE_URLS.BRANCH_REPORTS, id)}>
              <Button size='small' icon={<BarChart3 size={14} />}>
                {t('branches.reports')}
              </Button>
            </Link>
            {isAdmin && (
              <RowActions
                items={[
                  { key: 'edit', icon: Pencil, label: t('common.edit'), to: createLink(ROUTE_URLS.EDIT_BRANCH, id) },
                  { type: 'divider', key: 'd' },
                  {
                    key: 'delete',
                    icon: Trash2,
                    danger: true,
                    label: t('common.delete'),
                    onClick: () => {
                      setSelectedBranchId(id);
                      setShowDeleteModal(true);
                    },
                  },
                ]}
              />
            )}
          </div>
        ),
      },
    ],
    [getComponent, isAdmin, t],
  );

  const confirmHandler = async () => {
    resetState();
    await deleteBranch(selectedBranchId);
  };

  const resetState = () => {
    setShowDeleteModal(false);
    setSelectedBranchId(null);
  };

  return (
    <>
      <DeleteConfirmModal open={showDeleteModal} onConfirm={confirmHandler} onCancel={resetState} />
      <PageHeader
        title={t('branches.title')}
        subtitle={t('branches.subtitle')}
        actions={
          isAdmin && (
            <Link to={ROUTE_URLS.CREATE_BRANCH}>
              <Button type='primary' icon={<Plus size={16} />}>
                {t('branches.add')}
              </Button>
            </Link>
          )
        }
      />
      <div className='wm-toolbar'>
        <SearchInput value={search} onChange={setSearch} placeholder={t('branches.searchPlaceholder')} />
        <span className='wm-toolbar__grow' />
        {!isLoading && Array.isArray(branches) && (
          <span className='wm-badge wm-badge--plain'>{t('branches.count', { count: branches.length })}</span>
        )}
      </div>
      <DataTable
        dataSource={filtered}
        columns={columns}
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        emptyVariant={search ? 'search' : undefined}
      />
    </>
  );
};
